/**
 * Ligt er een goedgekeurde ruil op deze cel? — puur een OPZOEKING, geen berekening.
 *
 * De matrix leest maandroosters; daar is een goedgekeurde ruil NIET in verwerkt. De server
 * legt hem pas bij het LEZEN over de momentopname uit vastgelegdeWeken
 * (server/src/rooster-service.js:130-150, pasRuilWisselsToe), en dat is wat Teamrooster en
 * Mijn Rooster tonen. De teamleider ziet dus de oude naam zonder enig teken dat er iets mee
 * is gebeurd.
 *
 * Dit bestand lost dat op door te SIGNALEREN, niet door de uitkomst na te bouwen. Er wordt
 * niets gekopieerd, niets gemuteerd en geen wissel toegepast — alleen vijf velden vergeleken.
 * De reden om het zo te houden: pasRuilWisselsToe naar de client halen zou een tweede
 * implementatie van dezelfde logica opleveren, en dit project heeft daar al één van
 * (client/src/lib/divergentie.js:2-6, de bewuste tweelingkopie van de server). Eén is genoeg.
 *
 * De server blijft de enige die de uitkomst bepaalt. Teamrooster is leidend; deze markering
 * wijst er alleen naartoe.
 */

// Een dienst-slot is uniek op deze vijf. Zelfde definitie als `zelfdeDienst` op de server
// (server/src/index.js:460-467) en als de validatie bij het aanmaken van een ruilverzoek
// (index.js:1250-1252).
const SLOT_VELDEN = ['isoJaar', 'weeknummer', 'dag', 'gemeenteKey', 'dienstId'];

// Een slot telt alleen mee als het ALLE vijf velden heeft. Geen terugval op een ontbrekend
// veld: half matchen zou een markering op de verkeerde cel kunnen zetten, en dat is erger
// dan geen markering — de teamleider zou dan een ruil "zien" die er niet ligt.
function slotCompleet(slot) {
  return !!slot && typeof slot === 'object'
    && SLOT_VELDEN.every((veld) => slot[veld] !== undefined && slot[veld] !== null);
}

function zelfdeSlot(slot, isoJaar, weeknummer, gemeenteKey, dienstId, dag) {
  return slotCompleet(slot)
    && slot.isoJaar === isoJaar
    && slot.weeknummer === weeknummer
    && slot.dag === dag
    && slot.gemeenteKey === gemeenteKey
    && slot.dienstId === dienstId;
}

/**
 * @param {Array}  ruilWissels  store.ruilWissels — komt vers van de server (serverSync.js:84),
 *                              staat NIET in localStorage en wordt nooit teruggeschreven.
 * @param {number} isoJaar
 * @param {number} weeknummer
 * @param {string} gemeenteKey
 * @param {string} dienstId
 * @param {string} dag
 * @returns {{kant:'aangeboden'|'tegen', oud:string, nieuw:string, aantal:number}|null}
 *   null = geen ruil op dit slot.
 *   `oud`   = de naam die in de matrix (maandroosters) staat.
 *   `nieuw` = de naam die het team via de server ziet.
 *
 * DE RICHTING KOMT LETTERLIJK UIT rooster-service.js:145-148:
 *     vervang(w.aangeboden, w.indienerNaam, w.collegaNaam);   // aangeboden: indiener → collega
 *     vervang(w.tegen,      w.collegaNaam,  w.indienerNaam);  // tegen:      collega → indiener
 * Omdraaien wijst de verkeerde persoon aan.
 *
 * Bij MEERDERE wissels op hetzelfde slot (een dienst die iemand via een eerdere ruil kreeg,
 * kan opnieuw geruild zijn) wordt bewust de EERSTE teruggegeven: de lijst staat op volgorde
 * van goedkeuring (index.js:1479-1485), dus de eerste is degene wiens `oud` nog écht in de
 * matrix staat. De uiteindelijke naam na een ketting uitrekenen zou pasRuilWisselsToe
 * nabouwen — precies wat hier niet gebeurt. `aantal` meldt daarom hoeveel er liggen, zodat
 * de tooltip bij een ketting naar Teamrooster kan verwijzen in plaats van een half antwoord
 * te geven.
 */
export function ruilOpCel(ruilWissels, isoJaar, weeknummer, gemeenteKey, dienstId, dag) {
  if (!Array.isArray(ruilWissels) || ruilWissels.length === 0) return null;

  let eerste = null;
  let aantal = 0;

  for (const w of ruilWissels) {
    if (!w || typeof w !== 'object') continue;

    let treffer = null;
    if (zelfdeSlot(w.aangeboden, isoJaar, weeknummer, gemeenteKey, dienstId, dag)) {
      treffer = { kant: 'aangeboden', oud: w.indienerNaam, nieuw: w.collegaNaam };
    } else if (zelfdeSlot(w.tegen, isoJaar, weeknummer, gemeenteKey, dienstId, dag)) {
      treffer = { kant: 'tegen', oud: w.collegaNaam, nieuw: w.indienerNaam };
    }
    if (!treffer) continue;

    // Zonder allebei de namen is er niets zinnigs te tonen; zo'n record telt niet mee.
    if (typeof treffer.oud !== 'string' || typeof treffer.nieuw !== 'string') continue;

    aantal += 1;
    if (!eerste) eerste = treffer;
  }

  return eerste ? { ...eerste, aantal } : null;
}
